import React, { forwardRef, useImperativeHandle, RefAttributes, useState, useMemo, useCallback, useRef } from "react";
// Removed: import { useODataContext } from "../contexts/ODataContext";
import {
  Grid,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridToolbar,
  GridSelectionChangeEvent,
  getSelectedState,
  GridHeaderSelectionChangeEvent,
  GridColumnProps,
  GridRowClickEvent
} from "@progress/kendo-react-grid";
// Added: import { process } from "@progress/kendo-data-query";
import { State, process, CompositeFilterDescriptor } from "@progress/kendo-data-query";
import { getter } from "@progress/kendo-react-common";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export"; // Removed ExcelExportColumnProps as it wasn't used directly
import config from "../contexts/ConfigContext"; // Assuming config might still be needed for PAGE_SIZE default
import { Button } from "@progress/kendo-react-buttons";
import { Trans } from 'react-i18next';
// Removed: import { CompositeFilterDescriptor } from '@progress/kendo-data-query'; // Duplicate import
import { t } from "i18next";
import { PermissionButton } from "./PermissionButton";

export interface ColumnCustomProps extends GridColumnProps {
  isExportable?: boolean;
}

interface DataGridProps extends RefAttributes<DataGridHandle> {
  Columns: ColumnCustomProps[];
  DataItemKey: string;
  dataSource: any[]; // <-- New prop for data array
  onSelectedItemsChange?: (selectedItems: number[]) => void;
  Height?: string;
  ShowAddButton?: boolean;
  ShowRemoveButton?: boolean;
  ShowExportButton?: boolean;
  onAddButtonClick?: () => void;
  onRemoveButtonClick?: () => void;
  PermissionButtonCode?: string;
  PageSize?: number;
  onRowClick?: (event: GridRowClickEvent) => void;
  // Removed: autoBind?: boolean; // No longer needed as data comes from props
  editField?: string; // Keep for potential inline editing logic
  // Removed: onDataStateChange?: () => void; // Might be less relevant now, or could be kept if parent needs notification
  onDataStateChange?: (newState: State) => void; // Optional: pass the new state up
  ShowCustomButton?: boolean;
  onCustomButtonClick?: () => void;
  CustomButtonLabel?: string;
  enableCustomButton?: boolean;
  onDataChange?: (newData: any[]) => void; // <-- New prop for notifying parent about data changes (e.g., inline edit)

}

export type DataGridHandle = {
  // Removed: refreshData: () => void; // Data controlled externally
  filterData: (filter?: CompositeFilterDescriptor) => void; // Will now update local state
  orderData: (sort: any) => void; // Will now update local state
  editInline: (id: number) => void; // Needs modification
  cancelEditInline: (id: number) => void; // Needs modification
  clearSelection: () => void; // Added utility
};

const DataGridCustomCS: React.FC<DataGridProps> = forwardRef<DataGridHandle, DataGridProps>((props, ref) => {
  // --- State and Constants ---
  const SELECTED_FIELD: string = "selected";
  const pageSize = props.PageSize ? props.PageSize : config.PAGE_SIZE; // Use config or default
  const [dataState, setDataState] = useState<State>({ take: pageSize, skip: 0, sort: [], filter: undefined });
  const height = props.Height ? props.Height : "100%";
  const { editField, DataItemKey, dataSource = [], onDataChange } = props; // Destructure dataSource with default
  const idGetter = getter(DataItemKey);

  // --- Data Processing ---
  // Process the dataSource prop based on the current grid state (paging, sorting, filtering)
  const processedDataResult = useMemo(() => {
    return process(dataSource, dataState);
  }, [dataSource, dataState]);

  // --- Selection State ---
  const [selectedState, setSelectedState] = useState<{
    [id: string]: boolean | number[];
  }>({});

  // --- Imperative Handle ---
  useImperativeHandle(ref, () => ({
    // refreshData is removed as data is passed via props

    filterData: (filter?: CompositeFilterDescriptor) => {
      const newDataState = { ...dataState, filter: filter, skip: 0 }; // Reset skip on filter
      setDataState(newDataState);
       if (props.onDataStateChange) {
         props.onDataStateChange(newDataState);
       }
    },
    orderData: (sort: any) => {
      const newDataState = { ...dataState, sort: sort };
      setDataState(newDataState);
       if (props.onDataStateChange) {
         props.onDataStateChange(newDataState);
       }
    },
    // IMPORTANT: Inline editing directly modifies the source data.
    // This now requires notifying the parent component via `onDataChange`.
    editInline: (id: number) => {
      if (!editField || !onDataChange) {
        console.warn("editInline called but 'editField' or 'onDataChange' prop is missing.");
        return;
      }
      const newData = dataSource.map((item: any) =>
        idGetter(item) === id
          ? {
            ...item,
            [editField]: true, // Use editField prop
          }
          : item
      );
      onDataChange(newData); // Notify parent to update the dataSource prop
    },
    cancelEditInline: (id: number) => {
       if (!editField || !onDataChange) {
        console.warn("cancelEditInline called but 'editField' or 'onDataChange' prop is missing.");
        return;
      }
      // Optionally, you might want to revert changes here if needed,
      // but typically cancelling just turns off the edit mode.
       const newData = dataSource.map((item: any) =>
        idGetter(item) === id
          ? {
            ...item,
            [editField]: false, // Use editField prop
          }
          : item
      );
      onDataChange(newData); // Notify parent to update the dataSource prop
    },
    clearSelection: () => {
        setSelectedState({});
        if(props.onSelectedItemsChange) {
            props.onSelectedItemsChange([]);
        }
    }
  }), [dataState, dataSource, idGetter, editField, onDataChange, props.onSelectedItemsChange, props.onDataStateChange]); // Add dependencies


  // --- Event Handlers ---

  // Handle Kendo Grid data state change (paging, sorting, filtering)
  const dataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
     if (props.onDataStateChange) {
       props.onDataStateChange(e.dataState); // Notify parent of state change
     }
  };

  // Handle selection change
  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: DataItemKey, // Use prop directly
      });
      setSelectedState(newSelectedState);
      if (props.onSelectedItemsChange) {
        const selectedIds = Object.keys(newSelectedState)
            .filter((key) => newSelectedState[key] === true) // Only include keys that are true
            .map(Number); // Convert keys (which are strings) to numbers
        props.onSelectedItemsChange(selectedIds);
      }
    },
    [selectedState, DataItemKey, props.onSelectedItemsChange] // Updated dependencies
  );

  // Handle header selection change
  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement = event.syntheticEvent.target as HTMLInputElement;
      const checked = checkboxElement.checked;
      const newSelectedState: { [key: string]: boolean } = {};

      // Select/deselect items currently visible on the page
      processedDataResult.data.forEach((item) => { // Use processedDataResult.data
        newSelectedState[idGetter(item)] = checked;
      });

      // Merge with existing selection state for items not on the current page
      const finalSelectedState = { ...selectedState, ...newSelectedState };

      // If unchecking, ensure all are false; if checking, ensure all processed items are true
       const updatedState = checked
        ? finalSelectedState
        : Object.keys(selectedState).reduce((acc, key) => {
            // If the key corresponds to an item on the current page, use the newSelectedState value (false)
            // Otherwise, keep the existing value (which might be true for items on other pages)
            // Correction: If unchecking header, *all* should be unchecked unless complex state preservation is needed.
            // Simplest approach: Clear all if unchecking header.
            if (!checked) {
                // If unchecking the header, clear everything for simplicity,
                // unless complex multi-page selection persistence is required.
                // Let's clear all for now. If you need to preserve selection
                // across pages when unchecking header, this logic needs refinement.
                 return {};
            } else {
                 // If checking, apply selection to current page items
                 processedDataResult.data.forEach((item) => {
                    acc[idGetter(item)] = true;
                 });

            }
            return acc;
          }, {} as { [key: string]: boolean });


      setSelectedState(updatedState);

      if (props.onSelectedItemsChange) {
         const selectedIds = Object.keys(updatedState)
            .filter((key) => updatedState[key] === true)
            .map(Number);
        props.onSelectedItemsChange(selectedIds);
      }
    },
    [processedDataResult.data, idGetter, props.onSelectedItemsChange, selectedState] // Added selectedState
  );


  // --- Excel Export ---
  const _export = useRef<ExcelExport | null>(null);
  const _grid = useRef<any>(); // Keep ref for potential future use, though not strictly needed for export now

  const excelExport = () => {
    if (_export.current !== null) {
      // Export the entire original data source, ignoring grid state (paging/filtering)
      _export.current.save(dataSource); // Pass the full dataSource prop
    }
  };

  // --- Render ---
  return (
    <div>
      {/* ExcelExport uses the full dataSource */}
      <ExcelExport
      data={dataSource} // Provide full data source here
      ref={_export}
      >
      {props.Columns
        .filter(c => c.isExportable !== false) // Default to exportable unless explicitly false
        .map((column, index) => {
        // Ensure title defaults to field name if not provided, for Excel header
        const excelTitle = column.title || column.field || `Column_${index}`;
        return <ExcelExportColumn key={index} field={column.field} title={excelTitle} />
        })}
      </ExcelExport>

      <Grid
      ref={_grid}
      // Use the processed data for display
      data={{
        data: processedDataResult.data.map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
        })),
        total: processedDataResult.total, // Total is based on filtered data count
      }}
      {...dataState} // Spread the state for paging, sorting, filtering
      total={processedDataResult.total} // Total count after filtering
      style={{ height: height }} // Use dynamic height
      sortable={true}
      pageable={true}
      dataItemKey={DataItemKey} // Use prop
      selectedField={SELECTED_FIELD}
      pageSize={pageSize}
      filterable={true} // Enable filtering UI
      // Removed: columnMenuIcon={filterIcon} // Column menu is often enabled by default with filterable/sortable
      selectable={{
        enabled: true,
        drag: false,
        cell: false,
        mode: "multiple",
      }}
      onDataStateChange={dataStateChange} // Handle paging, sort, filter changes
      onSelectionChange={onSelectionChange}
      onHeaderSelectionChange={onHeaderSelectionChange}
      onRowClick={props.onRowClick}
      editField={editField} // Pass editField for Kendo's internal edit state if needed
      >
      {(props.ShowExportButton || props.ShowAddButton || props.ShowRemoveButton || props.ShowCustomButton) && (
        <GridToolbar>
          {props.ShowExportButton && (
        <Button
          title={t("general.exportToExcel")} // Use translation
          themeColor={"secondary"}
          type="button"
          onClick={excelExport}
          icon="file-excel" // Add icon
        >
          <Trans>general.exportToExcel</Trans>
        </Button>
          )}
          {props.ShowAddButton && (
        <PermissionButton code={props.PermissionButtonCode} mode="create">
          <Button
            title={t("general.add")}
            themeColor={"primary"}
            type="button"
            onClick={props.onAddButtonClick}
            icon="plus"
          >
            <Trans>general.add</Trans>
          </Button>
        </PermissionButton>
          )}
          {props.ShowRemoveButton && (
        <PermissionButton code={props.PermissionButtonCode} mode="edit">
          <Button
            title={t("general.remove")}
            themeColor={"error"}
            type="button"
            onClick={props.onRemoveButtonClick}
            icon="trash"
          >
            <Trans>general.remove</Trans>
          </Button>
        </PermissionButton>
          )}
          {props.ShowCustomButton && (
        <Button
          title={t(props.CustomButtonLabel ?? "")}
          themeColor={"primary"}
          type="button"
          disabled={!props.enableCustomButton}
          onClick={props.onCustomButtonClick}
        >
          <Trans>{props.CustomButtonLabel}</Trans>
        </Button>
          )}
        </GridToolbar>
      )}

      {/* Selection Column */}
      {(props.onSelectedItemsChange) && // Conditionally render based on if selection handler is provided
        <Column
        field={SELECTED_FIELD}
        width="65px"
        filterable={false} // Usually don't filter/sort selection column
        sortable={false}
         title=" " // Empty title for checkbox column often looks better
        headerSelectionValue={
           // Check if all items *currently displayed* are selected
           processedDataResult.data.length > 0 && // Ensure there's data to check
           processedDataResult.data.every((item) => selectedState[idGetter(item)])
        }
        />
      }

      {/* Data Columns */}
      {props.Columns.map((column, index) => {
        return <Column key={index} {...column} />
      })}

      </Grid>
    </div>
  );
});

export default DataGridCustomCS;